import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { axiosInstance } from '../../services/axiosInstance';
import MonitoringPoolAssignField from './MonitoringPoolAssignField';

interface AddAdminDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddAdminDialog: React.FC<AddAdminDialogProps> = ({
  open,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    confirmPassword: '',
    is_superuser: false,
    is_active: true,
    is_main_admin: false
  });
  const [monitoringPoolIds, setMonitoringPoolIds] = useState<number[]>([]);
  const [monitorAllPools, setMonitorAllPools] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_superuser' || name === 'is_active' || name === 'is_main_admin' ? checked : value
    }));
    if (name === 'is_superuser' && checked) {
      setMonitoringPoolIds([]);
      setMonitorAllPools(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (formData.password !== formData.confirmPassword) {
        setError('비밀번호가 일치하지 않음');
        return;
      }

      if (formData.password.length < 8) {
        setError('비밀번호는 8자리 이상');
        return;
      }

      const response = await axiosInstance.post('/api/users', {
        username: formData.username,
        email: formData.email,
        full_name: formData.full_name,
        password: formData.password,
        is_active: true,
        is_superuser: formData.is_superuser,
        is_main_admin: formData.is_main_admin
      });

      const newId = response?.data?.id;
      if (
        newId != null &&
        !formData.is_superuser &&
        (monitorAllPools || monitoringPoolIds.length > 0)
      ) {
        await axiosInstance.put(`/api/users/${newId}/monitoring-pools`, {
          pool_ids: monitorAllPools ? [] : monitoringPoolIds,
          monitor_all_pools: monitorAllPools,
        });
      }

      enqueueSnackbar('관리자가 추가됨', { variant: 'success' });
      onSuccess();
      onClose();
      setMonitoringPoolIds([]);
      setMonitorAllPools(false);
    } catch (error: any) {
      console.error('관리자 추가 오류:', error);
      let errorMessage = '관리자 추가에 실패';
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData)) {
          errorMessage = errorData.map((err: any) => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            if (err.message) return err.message;
            return JSON.stringify(err);
          }).join(', ');
        } else if (typeof errorData === 'object') {
          errorMessage = Object.entries(errorData)
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ');
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>관리자 추가</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="아이디"
            name="username"
            value={formData.username}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            fullWidth
            label="이메일"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />
          <TextField
            fullWidth
            label="이름"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            margin="normal"
            disabled={loading}
          />
          <TextField
            fullWidth
            label="비밀번호"
            name="password"
            type="password"
            value={formData.password}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
            inputProps={{ minLength: 8 }}
            helperText="비밀번호는 8자리 이상"
          />
          <TextField
            fullWidth
            label="비밀번호 확인"
            name="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            margin="normal"
            required
            disabled={loading}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_superuser}
                onChange={handleChange}
                name="is_superuser"
                disabled={loading}
              />
            }
            label="최고관리자 권한"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_main_admin}
                onChange={handleChange}
                name="is_main_admin"
                disabled={loading}
              />
            }
            label="메인 관리자"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleChange}
                name="is_active"
                disabled={loading}
              />
            }
            label="계정 활성화"
          />
          <MonitoringPoolAssignField
            adminId={null}
            selectedPoolIds={monitoringPoolIds}
            onChange={setMonitoringPoolIds}
            monitorAllPools={monitorAllPools}
            onMonitorAllPoolsChange={setMonitorAllPools}
            disabled={loading}
            hidden={formData.is_superuser}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            취소
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            추가
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddAdminDialog; 